import { normalizeUppercase } from "./utils";
import { isTransferTransaction, TRANSACTION_KINDS } from "./transaction-types";

export function normalizeAccountKey(value) {
  return normalizeUppercase(value);
}

export function getAccountNameMap(accounts = []) {
  const counts = new Map();
  const byName = new Map();

  accounts.forEach((account) => {
    const key = normalizeAccountKey(account.name);
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
    byName.set(key, account);
  });

  return { counts, byName };
}

export function findUniqueAccountByName(accounts = [], name) {
  const key = normalizeAccountKey(name);
  if (!key) return null;
  const { counts, byName } = getAccountNameMap(accounts);
  return counts.get(key) === 1 ? byName.get(key) || null : null;
}

export function findAccountByIdOrName(accounts = [], id, name) {
  if (id) {
    const byId = accounts.find((account) => account.id === id);
    if (byId) return byId;
  }
  return findUniqueAccountByName(accounts, name);
}

export function getTransactionAccountId(transaction = {}, accounts = []) {
  if (transaction.account_id) return transaction.account_id;
  return findUniqueAccountByName(accounts, transaction.account)?.id || null;
}

export function getTransferAccountIds(transaction = {}, linkedTransaction = null, accounts = []) {
  if (!isTransferTransaction(transaction)) {
    return { fromAccountId: null, toAccountId: null };
  }

  const fromAccountId = transaction.from_account_id ||
    linkedTransaction?.from_account_id ||
    (transaction.type === TRANSACTION_KINDS.EXPENSE ? transaction.account_id : linkedTransaction?.account_id) ||
    findUniqueAccountByName(accounts, transaction.from_account || linkedTransaction?.from_account)?.id ||
    (transaction.type === TRANSACTION_KINDS.EXPENSE
      ? findUniqueAccountByName(accounts, transaction.account)?.id
      : findUniqueAccountByName(accounts, linkedTransaction?.account)?.id) ||
    null;

  const toAccountId = transaction.to_account_id ||
    linkedTransaction?.to_account_id ||
    (transaction.type === TRANSACTION_KINDS.INCOME ? transaction.account_id : linkedTransaction?.account_id) ||
    findUniqueAccountByName(accounts, transaction.to_account || linkedTransaction?.to_account)?.id ||
    (transaction.type === TRANSACTION_KINDS.INCOME
      ? findUniqueAccountByName(accounts, transaction.account)?.id
      : findUniqueAccountByName(accounts, linkedTransaction?.account)?.id) ||
    null;

  return { fromAccountId, toAccountId };
}

export function transactionMatchesAccount(transaction = {}, account = {}, accounts = [], linkedTransaction = null) {
  if (!account?.id && !account?.name) return false;

  if (isTransferTransaction(transaction)) {
    const txAccountId = getTransactionAccountId(transaction, accounts);
    if (account.id && txAccountId) return txAccountId === account.id;

    const name = normalizeAccountKey(account.name);
    if (transaction.account) return normalizeAccountKey(transaction.account) === name;

    return Boolean(name) && [
      transaction.from_account,
      transaction.to_account,
      linkedTransaction?.from_account,
      linkedTransaction?.to_account,
      transaction.account,
      linkedTransaction?.account,
    ].some((value) => normalizeAccountKey(value) === name);
  }

  const txAccountId = getTransactionAccountId(transaction, accounts);
  if (account.id && txAccountId === account.id) return true;
  return normalizeAccountKey(transaction.account) === normalizeAccountKey(account.name);
}

export function getAccountDisplayName(transaction = {}, accounts = [], linkedTransaction = null) {
  if (isTransferTransaction(transaction)) {
    const { fromAccountId, toAccountId } = getTransferAccountIds(transaction, linkedTransaction, accounts);
    const fromAccount = findAccountByIdOrName(accounts, fromAccountId, transaction.from_account || linkedTransaction?.from_account);
    const toAccount = findAccountByIdOrName(accounts, toAccountId, transaction.to_account || linkedTransaction?.to_account);
    const fromName = fromAccount?.name || transaction.from_account || linkedTransaction?.from_account;
    const toName = toAccount?.name || transaction.to_account || linkedTransaction?.to_account;
    return fromName && toName ? `${fromName} -> ${toName}` : transaction.account || "—";
  }

  const account = findAccountByIdOrName(accounts, transaction.account_id, transaction.account);
  return account?.name || transaction.account || "—";
}

export function buildAccountIdBackfill(transactions = [], accounts = []) {
  return transactions.map((tx) => {
    const next = { ...tx };
    let changed = false;

    if (!next.account_id && next.account) {
      const account = findUniqueAccountByName(accounts, next.account);
      if (account?.id) {
        next.account_id = account.id;
        changed = true;
      }
    }

    if (isTransferTransaction(next)) {
      if (!next.from_account_id && next.from_account) {
        const account = findUniqueAccountByName(accounts, next.from_account);
        if (account?.id) {
          next.from_account_id = account.id;
          changed = true;
        }
      }
      if (!next.to_account_id && next.to_account) {
        const account = findUniqueAccountByName(accounts, next.to_account);
        if (account?.id) {
          next.to_account_id = account.id;
          changed = true;
        }
      }
    }

    return changed ? next : tx;
  });
}
